import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Icon } from "@/components/Icon";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { useSite, whatsappHref } from "@/context/SiteContext";
import { WhatsappLogo, MapPin, Lightning, ArrowRight, ArrowLeft, CheckCircle } from "@phosphor-icons/react";

const asList = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);

function Block({ title, items }) {
  if (!items.length) return null;
  return (
    <div className="mt-8">
      <h2 className="font-heading text-lg font-bold text-slate-900">{title}</h2>
      <ul className="mt-3 space-y-2.5">
        {items.map((r) => (
          <li key={r} className="flex items-start gap-3 text-[15px] leading-relaxed text-slate-700">
            <CheckCircle size={20} weight="fill" className="mt-0.5 shrink-0 text-brand-teal" /> {r}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const content = useSite();
  const [job, setJob] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/job-openings/${id}`).then((r) => setJob(r.data)).catch(() => setNotFound(true));
  }, [id]);

  const waOn = content.whatsapp_enabled !== false && (content.whatsapp_number || "").trim();

  if (notFound) return (
    <section className="bg-white py-24 text-center">
      <p className="text-slate-500">This position is no longer available.</p>
      <Link to="/careers" className="mt-4 inline-block font-semibold text-brand-purple">← Back to all openings</Link>
    </section>
  );
  if (!job) return <section className="bg-white py-24 text-center text-slate-400">Loading…</section>;

  const counties = job.counties || [];
  const area = counties.includes("Statewide") ? "Statewide (Pennsylvania)" : counties.length >= 10 ? "All CareDeserved service-area counties" : counties.join(", ");
  const applyTo = `/careers/apply?job=${job.id}`;

  return (
    <>
      <section className="relative overflow-hidden bg-brand-blue noise-overlay">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <Link to="/careers" data-testid="job-back" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-300 hover:text-white">
            <ArrowLeft size={16} weight="bold" /> All Openings
          </Link>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-brand-teal2">{job.category === "ODP" ? "ODP / Human Services" : "Transportation / NEMT"}</span>
            {job.urgent && <span className="inline-flex items-center gap-1 rounded-full bg-rose-500 px-3 py-1 text-xs font-bold text-white"><Lightning size={12} weight="fill" /> Urgently Hiring</span>}
          </div>
          <h1 data-testid="job-title" className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">{job.title}</h1>
          <div className="mt-5 flex flex-wrap gap-2">
            {(job.employment_types || []).map((t) => (
              <span key={t} className="rounded-full bg-brand-purple/90 px-3 py-1 text-sm font-semibold text-white">{t}</span>
            ))}
          </div>
          <p className="mt-4 inline-flex items-center gap-2 text-sm text-slate-200"><MapPin size={16} weight="fill" className="text-brand-teal2" /> {area}</p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Button data-testid="job-apply-now" onClick={() => navigate(applyTo)}
              className="h-13 rounded-full bg-brand-purple px-8 text-base font-semibold text-white hover:bg-brand-purple2">
              Apply Now <ArrowRight size={18} weight="bold" className="ml-2" />
            </Button>
            {waOn && (
              <a href={whatsappHref(content)} target="_blank" rel="noopener noreferrer"
                className="inline-flex h-13 items-center justify-center gap-2 rounded-full border-2 border-white/40 bg-white/5 px-6 text-base font-semibold text-white hover:bg-white/15">
                <WhatsappLogo size={20} weight="fill" className="text-[#25D366]" /> WhatsApp Us
              </a>
            )}
          </div>
        </div>
      </section>

      <section className="bg-white py-14 sm:py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          {job.summary && (
            <>
              <h2 className="font-heading text-lg font-bold text-slate-900">Position Summary</h2>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-700">{job.summary}</p>
            </>
          )}
          <Block title="Responsibilities" items={asList(job.responsibilities)} />
          <Block title="Minimum Qualifications" items={asList(job.qualifications)} />
          <Block title="Required Clearances / Credentials" items={asList(job.clearances)} />

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {job.license_required && (
              <div className="rounded-xl border border-slate-200 bg-brand-bg p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Driver's License</p>
                <p className="mt-1 text-sm text-slate-700">Valid driver's license with a safe driving record required.</p>
              </div>
            )}
            {job.experience && (
              <div className="rounded-xl border border-slate-200 bg-brand-bg p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Experience</p>
                <p className="mt-1 text-sm text-slate-700">{job.experience}</p>
              </div>
            )}
            {job.schedule && (
              <div className="rounded-xl border border-slate-200 bg-brand-bg p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Schedule / Availability</p>
                <p className="mt-1 text-sm text-slate-700">{job.schedule}</p>
              </div>
            )}
            {(job.compensation || "").trim() && (
              <div className="rounded-xl border border-slate-200 bg-brand-bg p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Compensation</p>
                <p className="mt-1 text-sm text-slate-700">{job.compensation}</p>
              </div>
            )}
          </div>

          <div className="mt-10 rounded-2xl bg-brand-blue p-8 text-center">
            <Logo variant="light" className="mx-auto h-9" />
            <p className="mt-4 font-heading text-xl font-bold text-white">Ready to apply for {job.title}?</p>
            <Button data-testid="job-apply-bottom" onClick={() => navigate(applyTo)}
              className="mt-5 h-13 rounded-full bg-brand-purple px-8 text-base font-semibold text-white hover:bg-brand-purple2">
              Apply Now <ArrowRight size={18} weight="bold" className="ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
